import { useState } from 'react';
import { useSelector } from 'react-redux';
import { Action } from '@interstate/components/Action';
import type { CheckBoxEventValue } from '@interstate/components/CheckBox';
import { CheckBox } from '@interstate/components/CheckBox';
import { Grid } from '@interstate/components/Grid';
import type { InterstateOnChangeEvent } from '@interstate/components/InterstateEvents';
import { Popover } from '@interstate/components/Popover';
import { Typography } from '@interstate/components/Typography';
import * as offerReduxSelectors from '@makemydeal/dr-dash-store/selectors/offerRedux';
import type { AddressData } from './CustomerAddressTypes';
import { extractCustomerAddressFields, getFormattedAddress, isAddressesEqual } from './CustomerAddressUtils';
import { PopoverRow, CheckBoxSection, AddressCopyContainer } from './CustomerAddresses.styles';
import { useCustomerAddress } from './CustomerAddressProvider';
import { kebabCase } from 'lodash';
import { ADDRESS_SEPARATOR } from '../constants';

const AddressTitle = {
    PRIMARY: 'Primary',
    BILLING: 'Billing'
};

const DisclaimerText = `(This will replace/override any existing co-buyer addresses)`;

export const BuyerAddressCopy = () => {
    const { handleCopyBuyerAddress, addresses, disabled } = useCustomerAddress();
    const buyer = useSelector(offerReduxSelectors.getShopperInfo);

    const primaryAddress: AddressData = extractCustomerAddressFields({ ...buyer, zip: buyer.zip ?? '' } as AddressData);
    const billingAddress: AddressData | undefined = buyer.billingAddress
        ? extractCustomerAddressFields(buyer.billingAddress as AddressData)
        : undefined;

    const [isChecked, setIsChecked] = useState(!isAddressesEqual(addresses, { primaryAddress, billingAddress }));

    const formattedPrimaryAddress = getFormattedAddress(primaryAddress);
    const formattedBillingAddress = billingAddress && getFormattedAddress(billingAddress);

    const handleCheckboxChange = (event: InterstateOnChangeEvent<CheckBoxEventValue>) => {
        const { checked } = event.target.value;
        setIsChecked(checked);
        if (checked) {
            handleCopyBuyerAddress({ primaryAddress, billingAddress });
        }
    };

    const addressRow = (title: string, address: string[]) => {
        return (
            <Typography
                tag="span"
                variant="h6"
                data-testid={kebabCase(`${title}-address-section`)}
                sx={{ fontWeight: 'semi-bold' }}
            >
                {`${title}:${ADDRESS_SEPARATOR}`}
                <Typography tag="span" variant="body-sm" sx={{ fontWeight: 'normal' }}>
                    {address.join(ADDRESS_SEPARATOR)}
                </Typography>
            </Typography>
        );
    };

    const buyerAddresses = () => {
        return (
            <Grid
                container
                flexDirection="column"
                sx={{
                    gap: '0.5rem'
                }}
            >
                <PopoverRow>{addressRow(AddressTitle.PRIMARY, formattedPrimaryAddress)}</PopoverRow>
                {billingAddress && <PopoverRow>{addressRow(AddressTitle.BILLING, formattedBillingAddress!)}</PopoverRow>}
            </Grid>
        );
    };

    return (
        <AddressCopyContainer data-testid="buyer-address-copy-container">
            <CheckBoxSection>
                <CheckBox
                    id="address-copy-checkbox"
                    checked={isChecked}
                    value=""
                    label={'Same as'}
                    onChange={handleCheckboxChange}
                    data-testid="address-copy-checkbox"
                    disabled={disabled}
                />
                <Popover
                    padding={true}
                    header="Buyer's Addresses"
                    data-testid="buyer-addresses-popover"
                    position="top-start"
                    trigger="outsideClick"
                    popoverContent={buyerAddresses()}
                    variant="withPointer"
                >
                    <Action data-testid="view-buyer-addresses-button" size="sm" disabled={disabled}>
                        Buyer's
                    </Action>
                </Popover>
                <Typography tag="span" variant="body-md">
                    Addresses
                </Typography>
            </CheckBoxSection>
            <Typography
                tag="span"
                variant="body-sm"
                sx={{
                    fontStyle: 'italic'
                }}
            >
                {DisclaimerText}
            </Typography>
        </AddressCopyContainer>
    );
};
